import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfDay, endOfDay, format } from "date-fns";

interface MetricsFilters {
  productId?: string;
  startDate: Date;
  endDate: Date;
}

interface RevenueTimeSeriesData {
  month: string;
  revenue: number;
  target: number;
}

interface ChurnTimeSeriesData {
  month: string;
  churn: number;
  target: number;
}

export const useMetricsTimeSeries = (filters: MetricsFilters) => {
  return useQuery({
    queryKey: ["metrics-timeseries", filters.productId, filters.startDate, filters.endDate],
    queryFn: async (): Promise<{ revenueData: RevenueTimeSeriesData[], churnData: ChurnTimeSeriesData[] }> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: userRole } = await supabase
        .from("user_roles")
        .select("tenant_id")
        .eq("user_id", user.id)
        .single();

      if (!userRole) throw new Error("No tenant found");

      const startDateStr = startOfDay(filters.startDate).toISOString();
      const endDateStr = endOfDay(filters.endDate).toISOString();

      // Query para métricas do período
      let query = supabase
        .from("metrics")
        .select("*")
        .eq("tenant_id", userRole.tenant_id)
        .gte("period_start", startDateStr)
        .lte("period_end", endDateStr)
        .order("period_start", { ascending: true });

      if (filters.productId) {
        query = query.eq("product_id", filters.productId);
      }

      const { data: metrics, error } = await query;
      if (error) throw error;

      // Agrupar métricas por mês
      const groupedByMonth = new Map<string, { revenue: number[], churn: number[] }>();

      metrics?.forEach(metric => {
        const monthKey = format(new Date(metric.period_start), "MMM");
        
        if (!groupedByMonth.has(monthKey)) {
          groupedByMonth.set(monthKey, { revenue: [], churn: [] });
        }

        const group = groupedByMonth.get(monthKey)!;
        
        if (metric.metric_type === "revenue" || metric.metric_type === "mrr") {
          group.revenue.push(Number(metric.value));
        } else if (metric.metric_type === "churn") {
          group.churn.push(Number(metric.value));
        }
      });

      // Converter para array de dados do gráfico
      const revenueData: RevenueTimeSeriesData[] = [];
      const churnData: ChurnTimeSeriesData[] = [];

      groupedByMonth.forEach((values, month) => {
        const avgRevenue = values.revenue.length > 0
          ? values.revenue.reduce((sum, v) => sum + v, 0) / values.revenue.length
          : 0;
        
        const avgChurn = values.churn.length > 0
          ? values.churn.reduce((sum, v) => sum + v, 0) / values.churn.length
          : 0;

        revenueData.push({
          month,
          revenue: Math.round(avgRevenue),
          target: Math.round(avgRevenue * 1.1), // Meta 10% acima
        });

        churnData.push({
          month,
          churn: Number(avgChurn.toFixed(1)),
          target: 3.0, // Meta de 3%
        });
      });

      return { revenueData, churnData };
    },
  });
};
